import { useCallback } from 'react';
import { useFormikContext } from 'formik-2';

/**
 * Patch one or more fields of a single form tab in a single Formik state transition.
 *
 * Why a single transition matters: each `setFieldValue` call runs `validate()` against
 * `setIn(lastRender.values, path, value)`, so two sequential `setFieldValue` calls each see
 * the *other* field as still-stale. Cross-field rules (e.g. "if enable_event_mapping=true,
 * event_names is required") then skip on the later call and the form ends up in an inconsistent
 * error state until something forces a fresh full-render validate (e.g. switching tabs).
 *
 * Pass either an object patch — merged into the previous tab object — or a function that
 * receives `prevTab` and returns the full next tab object. Use the function form when the
 * next state depends on the previous tab values.
 *
 * @param {string} tabId The form tab key under `values.formData`.
 * @returns {Function} `update(patch)` that applies `patch` to that tab.
 */
export function useTabUpdater(tabId) {
    const { setValues } = useFormikContext();
    return useCallback(
        patch => {
            setValues(prev => {
                const prevTab = prev.formData?.[tabId] || {};
                const nextTab = typeof patch === 'function' ? patch(prevTab) : { ...prevTab, ...patch };
                return {
                    ...prev,
                    formData: {
                        ...(prev.formData || {}),
                        [tabId]: nextTab,
                    },
                };
            });
        },
        [tabId, setValues]
    );
}

/**
 * Patch `values.formData` across multiple tabs in a single Formik state transition. Same
 * rationale as {@link useTabUpdater}; use this when the update spans more than one tab
 * (e.g. clearing postback tabs when receive_postbacks toggles off).
 *
 * Pass either an object merged into the previous formData, or a function `prevFormData =>
 * nextFormData` for prev-aware updates that read other tabs.
 *
 * @returns {Function} `update(patch)` that applies `patch` to formData.
 */
export function useFormDataUpdater() {
    const { setValues } = useFormikContext();
    return useCallback(
        patch => {
            setValues(prev => {
                const prevFormData = prev.formData || {};
                const nextFormData = typeof patch === 'function' ? patch(prevFormData) : { ...prevFormData, ...patch };
                return { ...prev, formData: nextFormData };
            });
        },
        [setValues]
    );
}
